import { useReactiveVar } from "@apollo/client";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { CATEGORY_ALL } from "@/components/game-categories/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { INTERCOM_TOUR_STEPS } from "@/helpers/constants";
import { type LocationType, regionByCoords } from "@/helpers/location";

import useCurrentLocation from "@/hooks/use-current-location";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";
import { exploreCategory } from "@/store";

import { LocationIcon, QueryIcon, SearchIcon } from "./icons";
import LocationInput from "./location-input";

interface SearchFormControlProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
  IconComponent: React.ElementType<React.ComponentProps<"svg">>;
  id: string;
  inputProps?: React.ComponentProps<"input">;
  label: string;
  size?: "small";
}

function SearchFormControl({
  className,
  children,
  IconComponent,
  id,
  inputProps = {},
  label,
  size,
  ...props
}: SearchFormControlProps) {
  const isSmall = size === "small";

  return (
    <div
      className={cn(
        "relative w-full border-pale-silver border-b pb-[13px] pl-9 md:border-b-0 md:pb-0",
        className
      )}
      {...props}
    >
      <IconComponent
        className={cn("absolute top-[13px] left-0", isSmall && "md:top-[9px]")}
      />
      <Label
        className={cn(
          "text-13 text-heather leading-[1.42em]",
          isSmall && "-mb-[5px] md:text-12 md:leading-[1.54em]"
        )}
        htmlFor={id}
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

export type SearchFormProps = {
  className?: string;
  onSearchFormVisibilityChange?: (hidden: boolean) => void;
  onSubmit?: () => void;
  size?: "small";
  useIconButton?: boolean;
  withFocus?: boolean;
} & React.ComponentProps<"div">;

export default function SearchForm({
  className,
  onSearchFormVisibilityChange,
  onSubmit,
  size,
  useIconButton = false,
  withFocus = false,
  ...props
}: SearchFormProps) {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const asPath = useRouterState({ select: (s) => s.location.href });

  const [searchQuery, setSearchQuery] = useState<string>(search.query ?? "");
  const [tmpLoading, setTmpLoading] = useState(true);
  const [tmpLocation, setTmpLocation] = useState<LocationType>({
    address: "",
    location: "",
  });

  const formRef = useRef<HTMLFormElement>(null);
  const queryRef = useRef<HTMLInputElement>(null);
  const searchFormPosition = useRef<number>(0);
  const lastVisibilityState = useRef<boolean>(false);

  const { location, loading, toExploreUrl } = useCurrentLocation();
  const category = useReactiveVar(exploreCategory);

  const handleLocation = (newLocation: LocationType) => {
    setTmpLocation(newLocation);
    setTmpLoading(false);
  };

  const handleProgress = () => setTmpLoading(true);

  const handleScroll = () => {
    if (!onSearchFormVisibilityChange) {
      return;
    }

    const { current } = searchFormPosition;

    if (!current) {
      return;
    }

    const currentState = window.scrollY >= current;

    if (!!lastVisibilityState.current !== currentState) {
      lastVisibilityState.current = currentState;
      onSearchFormVisibilityChange(currentState);
    }
  };

  const handleSearchFocus = (event: React.FocusEvent<HTMLInputElement>) =>
    setTimeout(() => event.target.select(), 1); // NOTE - Safari hack

  const handleSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchQuery(event.target.value);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    sessionStorage.removeItem("scrollPosition");

    const { current } = formRef;

    if (!current) {
      return;
    }

    const formData = Object.fromEntries(new FormData(current));

    if (!formData.categories && category && category !== CATEGORY_ALL) {
      formData.categories = category;
    }

    navigate({
      search: formData,
      to: "/explore",
    });

    if (typeof onSubmit === "function") {
      onSubmit();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: its fine
  useEffect(() => {
    if (!onSearchFormVisibilityChange) {
      return;
    }

    const { current } = formRef;

    if (!current) {
      return;
    }

    const { bottom } = current.getBoundingClientRect();
    searchFormPosition.current =
      bottom + document.documentElement.scrollTop - 100;

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [formRef]);

  useEffect(() => {
    if (withFocus && queryRef.current) {
      queryRef.current.focus();
    }
  }, [withFocus]);

  useEffect(() => {
    const [path] = asPath.split("?");
    const segments = path.slice(1).split("/");

    if (segments[0] === "explore") {
      (async () => {
        let address = "Unknown location";
        if (segments.length >= 2) {
          address = decodeURIComponent(segments[1]);
        } else if (typeof search.location === "string") {
          address = await regionByCoords(search.location);
        }

        setTmpLocation({
          address,
          location: typeof search.location === "string" ? search.location : "",
        });
        setSearchQuery(typeof search.query === "string" ? search.query : "");
        setTmpLoading(false);
      })();
      return;
    }

    if (!loading) {
      setTmpLocation(location);
      setTmpLoading(false);
    }
  }, [asPath, search, loading, location]);

  const extraFields = useMemo(() => {
    const fields: React.ReactNode[] = [];
    const fieldsBlackList = [
      "boundingBox",
      "location",
      "params",
      "path",
      "query",
    ];

    return Object.entries(search).reduce((accum, [field, value]) => {
      if (!value) {
        return accum;
      }
      if (fieldsBlackList.includes(field)) {
        return accum;
      }
      if (typeof value === "string") {
        accum.push(
          <input key={field} name={field} type="hidden" value={value} />
        );
      } else {
        accum.push(
          <input key={field} name={field} type="hidden" value={String(value)} />
        );
      }
      return accum;
    }, fields);
  }, [search]);

  const isSmall = size === "small";

  const queryId = useId();
  const locationId = useId();

  return (
    <form
      action={toExploreUrl(true, tmpLocation)}
      autoComplete="off"
      autoCorrect="off"
      onSubmit={handleSubmit}
      ref={formRef}
      spellCheck="false"
    >
      <div
        className={cn(
          "sticky top-20 flex flex-col rounded-[20px] bg-white p-5 md:min-w-[527px] md:flex-row md:items-center md:rounded-[100px]",
          isSmall ? "md:p-[7px] md:pl-[21px]" : "md:p-2.5 md:pl-7.5",
          className
        )}
        {...props}
      >
        <SearchFormControl
          IconComponent={QueryIcon}
          id={queryId}
          label="Search"
          size={size}
        >
          <Input
            aria-autocomplete="none"
            autoComplete="off"
            autoCorrect="off"
            className={cn(
              INTERCOM_TOUR_STEPS.searchInput,
              "mt-[3px] h-6 rounded-none border-0 p-0 text-16 text-black shadow-none focus-visible:ring-0 md:text-16",
              isSmall && "md:mt-0 md:h-5.5"
            )}
            id={queryId}
            name="query"
            onChange={handleSearchQuery}
            onFocus={handleSearchFocus}
            placeholder="Attractions"
            ref={queryRef}
            spellCheck="false"
            type="search"
            value={searchQuery}
          />
        </SearchFormControl>
        <Separator
          className={cn(
            "hidden bg-pale-silver md:block",
            isSmall
              ? "mr-[17px] data-[orientation=vertical]:h-[31px]"
              : "mr-[25px] data-[orientation=vertical]:h-[45px]"
          )}
          orientation="vertical"
        />
        <SearchFormControl
          className="mt-[15px] md:mt-0"
          IconComponent={LocationIcon}
          id={locationId}
          label="Near"
          size={size}
        >
          <LocationInput
            id={locationId}
            location={tmpLocation}
            onLocation={handleLocation}
            onProgress={handleProgress}
            size={size}
          />
        </SearchFormControl>
        {useIconButton ? (
          <Button
            aria-label="Search"
            className={cn(
              INTERCOM_TOUR_STEPS.searchButton,
              "size-9.5 shrink-0 rounded-full"
            )}
            disabled={tmpLoading}
            type="submit"
            variant="cta"
          >
            <SearchIcon />
          </Button>
        ) : (
          <Button
            className={cn(
              INTERCOM_TOUR_STEPS.searchButton,
              "mt-5 h-12.5 w-full shrink-0 rounded-[25px] md:mt-0",
              isSmall ? "md:h-[37px] md:w-[97px]" : "md:w-35"
            )}
            disabled={tmpLoading}
            type="submit"
            variant="cta"
          >
            {tmpLoading ? <Loader2Icon className="animate-spin" /> : "Search"}
          </Button>
        )}
      </div>
      <input
        name="location"
        type="hidden"
        value={tmpLocation?.location || location.location}
      />
      {extraFields}
    </form>
  );
}
